import { Add, EventBusy } from "@mui/icons-material";
import { Alert, Box, Button, LinearProgress, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import SortedDataGridPro6 from "../../../../../../design/sorted-datagrid-pro-6";
import { Holiday, HolidayScope } from "../../../../../holidays/types";
import useHolidayColumns from "../../controller/use-holiday-columns";

interface HolidaysTableProps {
	scope: HolidayScope;
	title: string;
	holidays: Holiday[];
	isLoading: boolean;
	locationName?: string;
	canAddHoliday: boolean;
	canManageCompanyHolidays: boolean;
	onAdd: () => void;
	onEdit: (holiday: Holiday) => void;
	onRemove: (holiday: Holiday) => void;
}

const EmptyState = () => {
	const { t } = useTranslation();
	return (
		<Box
			sx={{
				height: "100%",
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				gap: "8px",
				padding: "24px",
				color: "rgba(0, 0, 0, 0.38)",
			}}
		>
			<EventBusy sx={{ fontSize: 36 }} />
			<Typography sx={{ fontSize: "14px", textAlign: "center" }}>
				{t("holidays_empty")}
			</Typography>
		</Box>
	);
};

const LoadingOverlay = () => <LinearProgress />;

const HolidaysTable = ({
	scope,
	title,
	holidays,
	isLoading,
	locationName,
	canAddHoliday,
	canManageCompanyHolidays,
	onAdd,
	onEdit,
	onRemove,
}: HolidaysTableProps) => {
	const { t } = useTranslation();
	const columns = useHolidayColumns({
		scope,
		locationName,
		canManageCompanyHolidays,
		onEdit,
		onRemove,
	});

	return (
		<Box
			sx={{
				border: "1px solid var(--divider, rgba(0, 0, 0, 0.12))",
				borderRadius: "8px",
				backgroundColor: "#fff",
				padding: "20px",
				marginTop: "16px",
			}}
		>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: "16px",
					paddingTop: "20px",
					paddingBottom: "20px",
				}}
			>
				<Typography
					component="h3"
					sx={{
						fontFamily: "Roboto",
						fontWeight: 400,
						fontSize: "16px",
						lineHeight: "24px",
						color: "#000000DE",
					}}
				>
					{title}
				</Typography>
				{canAddHoliday ? (
					<Button
						disableElevation
						sx={{ minHeight: 36, flexShrink: 0 }}
						variant="outlined"
						color="primary"
						onClick={onAdd}
						startIcon={<Add />}
					>
						{t("holidays_add-button")}
					</Button>
				) : null}
			</Box>
			<Box sx={{ padding: "12px 8px" }}>
				<SortedDataGridPro6
					columns={columns}
					rows={holidays}
					defaultSortField="date"
					disableColumnSelector
					disableDensitySelector
					disableColumnMenu
					pagination
					initialState={{
						pagination: { paginationModel: { pageSize: 10 } },
						filter: {},
					}}
					pageSizeOptions={[10, 25, 50]}
					autoHeight
					getRowHeight={() => "auto"}
					loading={isLoading}
					slots={{
						loadingOverlay: LoadingOverlay,
						noRowsOverlay: EmptyState,
					}}
					sx={{
						border: "none !important",
						minHeight: holidays.length === 0 ? 220 : undefined,
						".MuiDataGrid-cell": {
							"&:focus": { outline: "none" },
							"&.hover-column-cell": {
								position: "sticky",
								right: 0,
								zIndex: 1,
							},
							"& .hover-content": {
								transition: "opacity 0.3s ease-in-out",
								opacity: 0,
								backgroundColor: "transparent",
							},
						},
						".MuiDataGrid-cell:focus-within": { outline: "none" },
						"& .MuiDataGrid-columnHeader:focus-within": { outline: "none" },
						".MuiDataGrid-row:hover .hover-column-cell .hover-content, .MuiDataGrid-cell:focus-within .hover-content":
							{
								opacity: 1,
								backgroundColor: "transparent",
							},
					}}
				/>
				{scope === "location" ? (
					<Alert severity="info" sx={{ mt: "12px" }}>
						{t("holidays_location-footer-note", { location: locationName })}
					</Alert>
				) : null}
			</Box>
		</Box>
	);
};

export default HolidaysTable;
